import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { company } from '../../site/company';
import { BrandLogo } from './brand-logo';

const SERVICES: readonly TranslationKey[] = [
    'site.footer.svc1',
    'site.footer.svc2',
    'site.footer.svc3',
    'site.footer.svc4',
    'site.footer.svc5'
];

/**
 * The landing page's own footer, in the live site's dark treatment.
 *
 * **The year is interpolated rather than written into the translation.** The live page hard-codes
 * `2026` in both languages, which is a string that goes stale silently and does so twice.
 */
export function HomeFooter() {
    const { t } = useTranslation();
    const language = useLanguage();

    return (
        <footer className="footer">
            <div className="container">
                <div className="footer-grid">
                    <div>
                        <div className="footer-logo">
                            <BrandLogo stopColor="var(--brand)" />
                        </div>
                        <p className="footer-desc">{t('site.footer.desc')}</p>
                        <div className="footer-social">
                            <a
                                href={company.youTube}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="social-link"
                                aria-label="YouTube"
                            >
                                <img src="/media/icons/youtube.svg" alt="YouTube" />
                            </a>
                            <a
                                href={company.instagram}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="social-link"
                                aria-label="Instagram"
                            >
                                <img src="/media/icons/instagram.svg" alt="Instagram" />
                            </a>
                            <a
                                href={company.tikTok}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="social-link"
                                aria-label="Tiktok"
                            >
                                <img src="/media/icons/tiktok.svg" alt="TikTok" />
                            </a>
                        </div>
                    </div>
                    <div>
                        <div className="footer-col-title">{t('site.footer.nav')}</div>
                        <ul className="footer-links">
                            <li>
                                <a href="#home">{t('site.nav.home')}</a>
                            </li>
                            <li>
                                <a href="#about">{t('site.nav.about')}</a>
                            </li>
                            <li>
                                <a href="#mortgage-programs">{t('site.nav.programs')}</a>
                            </li>
                            <li>
                                <a href="#faq">{t('site.footer.faq')}</a>
                            </li>
                            <li>
                                <a href="#socials">{t('site.footer.socials')}</a>
                            </li>
                            <li>
                                <a href="#partners">{t('site.footer.partners')}</a>
                            </li>
                            <li>
                                <a href="#contact">{t('site.nav.contact')}</a>
                            </li>
                            {/*
                                The only entry in this column that leaves the page, which is why
                                it is a router link and why it comes last: everything above it is
                                a section of this page, in the order the page renders them.
                            */}
                            <li>
                                <Link to={`/${language}/blog`}>{t('nav.blog')}</Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <div className="footer-col-title">{t('site.footer.services')}</div>
                        <ul className="footer-links">
                            {SERVICES.map((service) => (
                                <li key={service}>
                                    <a href="#contact">{t(service)}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p className="footer-copy">
                        {t('site.footer.copy').replace('{year}', String(new Date().getFullYear()))}{' '}
                        <Link to={`/${language}/policy`}>{t('site.footer.policy')}</Link> ·{' '}
                        <Link to={`/${language}/policy`}>{t('site.footer.terms')}</Link>
                    </p>
                    <p className="footer-tagline">{t('site.footer.tagline')}</p>
                </div>
            </div>
        </footer>
    );
}
